import React from "react";
import { Link } from "react-router-dom";
import { Star, ArrowRight, Phone, MessageSquare } from "lucide-react";
import HeroSlider from "../components/HeroSlider";
import EstimateForm from "../components/EstimateForm";
import LocationCard from "../components/LocationCard";
import useSeo from "../hooks/useSeo";
import useReveal from "../hooks/useReveal";
import {
  seo,
  reviewsBanner,
  homeServices,
  homeFeatures,
  badges,
  business,
  social,
  googleReviewUrl,
} from "../data/siteData";

export default function Home() {
  useSeo(seo["/"].title, seo["/"].description);
  useReveal();

  return (
    <>
      <HeroSlider />

      {/* Customer reviews banner */}
      <section className="bg-[#244aa2]" data-testid="home-reviews-banner">
        <div className="mr-container py-9 text-center">
          <h2 className="font-head text-xl font-extrabold text-white sm:text-2xl">
            <Link
              to={reviewsBanner.to}
              data-testid="home-reviews-banner-heading"
              className="inline-flex flex-wrap items-center justify-center gap-2 transition-colors hover:text-[#cfe0ff]"
            >
              <Star className="h-6 w-6 shrink-0 fill-[#ffd166] text-[#ffd166]" />
              {reviewsBanner.headline}
            </Link>
          </h2>
          <Link
            to={reviewsBanner.to}
            data-testid="home-reviews-banner-button"
            className="mt-5 inline-block bg-white px-7 py-3 text-[12px] font-bold uppercase tracking-[0.1em] text-[#244aa2] transition-transform duration-200 hover:-translate-y-0.5"
          >
            {reviewsBanner.cta}
          </Link>
        </div>
      </section>

      {/* Estimate form on dark band (mirrors live homepage form block) */}
      <section className="bg-[#0c2a66] py-16">
        <div className="mr-container">
          <div className="reveal mx-auto max-w-3xl">
            <EstimateForm variant="hero" title="Get A Free Estimate" />
          </div>
        </div>
      </section>

      {/* 4 service cards */}
      <section className="bg-white py-16">
        <div className="mr-container-wide">
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {homeServices.map((s) => (
              <article key={s.title} className="reveal group text-center">
                <Link to={s.to} className="block overflow-hidden bg-neutral-100 shadow-sm">
                  <div className="aspect-square overflow-hidden">
                    <img
                      src={s.image}
                      alt={s.title}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-[600ms] ease-out group-hover:scale-110"
                    />
                  </div>
                </Link>
                <h3 className="mr-h3 mt-5 text-lg">
                  <Link to={s.to} className="transition-colors hover:text-[#a10000]">
                    {s.title}
                  </Link>
                </h3>
                <p className="mt-3 text-[15px] leading-relaxed text-[#575757]">{s.desc}</p>
                <Link
                  to={s.to}
                  className="mt-4 inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-wider text-[#244aa2] transition-colors hover:text-[#a10000]"
                >
                  Learn more
                  <ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-1" />
                </Link>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Two long-form feature blocks */}
      {homeFeatures.map((f, i) => (
        <section
          key={f.headline}
          className={i % 2 === 0 ? "bg-[#f7f7f7] py-16" : "bg-white py-16"}
        >
          <div className="mr-container-wide">
            <div
              className={`grid items-center gap-10 lg:grid-cols-2 ${
                i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""
              }`}
            >
              <div className="reveal overflow-hidden shadow-lg">
                <img
                  src={f.image}
                  alt={f.headline}
                  loading="lazy"
                  className="h-full max-h-[440px] w-full object-cover transition-transform duration-[700ms] hover:scale-105"
                />
              </div>
              <div className="mr-body reveal">
                <h2 className="mr-h2 text-2xl sm:text-[30px]">{f.headline}</h2>
                <h3 className="font-head mt-3 text-[15px] font-bold uppercase tracking-wide text-[#a10000]">
                  {f.subhead}
                </h3>
                <div className="mt-5">
                  {f.paragraphs.map((p) => (
                    <p key={p.slice(0, 30)}>{p}</p>
                  ))}
                  {!!f.bullets.length && (
                    <ul className="mt-2 space-y-2">
                      {f.bullets.map((b) => (
                        <li key={b.label} className="flex items-start gap-2">
                          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#a10000]" />
                          <Link to={b.to} className="font-semibold">
                            {b.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="mt-7 flex flex-wrap gap-3">
                  <Link
                    to="/contact-us"
                    className="bg-[#244aa2] px-7 py-3 text-[12px] font-bold uppercase tracking-[0.1em] text-white transition-colors duration-200 hover:bg-[#0c2a66]"
                  >
                    Contact Us
                  </Link>
                  <a
                    href={`sms:1${business.phoneDigits}`}
                    className="flex items-center gap-2 border-2 border-[#244aa2] px-7 py-3 text-[12px] font-bold uppercase tracking-[0.1em] text-[#244aa2] transition-colors duration-200 hover:bg-[#244aa2] hover:text-white"
                  >
                    <MessageSquare className="h-4 w-4" /> Text {business.phone}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* Reviews / badges + location, as on live homepage bottom */}
      <section className="bg-[#f7f7f7] py-16">
        <div className="mr-container-wide grid gap-10 lg:grid-cols-3">
          <div className="reveal lg:col-span-2">
            <h2 className="mr-h2 text-2xl sm:text-3xl">
              <a href={googleReviewUrl} target="_blank" rel="noreferrer">
                {business.name}
              </a>
            </h2>
            <p className="font-head mt-2 text-lg font-bold text-[#242424]">
              {business.address.line1} {business.address.line2} {business.address.city},{" "}
              {business.address.state} {business.address.zip}
            </p>
            <img
              src={badges.googleStars}
              alt="Mr. Resurface LLC Google review rating"
              className="mt-5 max-w-[320px]"
            />
            <div className="mt-7 flex flex-wrap items-center gap-5">
              <a href={googleReviewUrl} target="_blank" rel="noreferrer">
                <img
                  src={badges.reviewUs}
                  alt="Review us on Google"
                  className="h-14 w-auto transition-transform duration-200 hover:scale-105"
                />
              </a>
              <a href={social[3].url} target="_blank" rel="noreferrer">
                <img
                  src={badges.angiesList}
                  alt="Angie's List reviews for Mr. Resurface LLC"
                  className="h-14 w-auto transition-transform duration-200 hover:scale-105"
                />
              </a>
              <a href={social[2].url} target="_blank" rel="noreferrer">
                <img
                  src={badges.bbbSeal}
                  alt="Mr Resurface, LLC is a BBB Accredited Business"
                  className="h-16 w-auto transition-transform duration-200 hover:scale-105"
                />
              </a>
              <a href={social[0].url} target="_blank" rel="noreferrer">
                <img
                  src={badges.facebookBtn}
                  alt="Follow us on Facebook"
                  className="h-11 w-auto transition-transform duration-200 hover:scale-105"
                />
              </a>
              <a href={social[1].url} target="_blank" rel="noreferrer">
                <img
                  src={badges.twitter}
                  alt="Follow Mr. Resurface on Twitter"
                  className="h-11 w-auto transition-transform duration-200 hover:scale-105"
                />
              </a>
            </div>
            <a
              href={`tel:${business.phoneRaw}`}
              className="mt-8 inline-flex items-center gap-2 text-2xl font-extrabold text-[#244aa2] transition-colors hover:text-[#a10000]"
            >
              <Phone className="h-6 w-6" /> {business.phone}
            </a>
          </div>
          <div className="reveal">
            <LocationCard />
          </div>
        </div>
      </section>
    </>
  );
}
